import { useState, useEffect, useCallback } from 'preact/hooks'
import { touchPointsApi } from '@/api/touchPoints'
import { parseTranscriptLines, type TranscriptLine } from '@/utils/meetingTranscript'

export type TranscriptState = 'idle' | 'loading' | 'ready' | 'error' | 'unreadable'

/**
 * Fetch + parse a meeting's transcript into timed lines for the meeting page (ORB-436). Mints a fresh
 * transcript SAS on demand (so a deep-linked/reloaded page never depends on the stale SAS carried in the
 * list payload), then fetches and parses the JSON. Exposes the minted URL for a raw-JSON fallback link.
 */
export function useMeetingTranscript(workspaceId: string, touchPointId: string, hasTranscript: boolean) {
  const [state, setState] = useState<TranscriptState>('idle')
  const [lines, setLines] = useState<TranscriptLine[]>([])
  const [url, setUrl] = useState('')

  const load = useCallback(async () => {
    if (!hasTranscript) {
      setState('idle')
      setLines([])
      setUrl('')
      return
    }
    setState('loading')
    try {
      const sas = await touchPointsApi.getTranscriptUrl(workspaceId, touchPointId)
      const sasUrl = sas.data?.Url
      if (!sasUrl) throw new Error('no-sas')
      setUrl(sasUrl)
      const response = await fetch(sasUrl)
      if (!response.ok) throw new Error(String(response.status))
      const parsed = parseTranscriptLines(await response.json())
      if (!parsed || parsed.length === 0) {
        setState('unreadable')
        return
      }
      setLines(parsed)
      setState('ready')
    } catch {
      setState('error')
    }
  }, [workspaceId, touchPointId, hasTranscript])

  useEffect(() => { void load() }, [load])

  return { state, lines, url, reload: load }
}
